import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { LogOut, Users, Shield, Newspaper, Trophy, CalendarDays, CalendarCheck, Settings, KeyRound, Package, UserRound, FileText, Radar, BarChart3, UserPlus, HandCoins, Printer, Briefcase, ChevronRight, Film, Receipt, Goal, Mail, Wallet, ListChecks, Copy } from "lucide-react";
import AdminRegistrations from "./AdminRegistrations";
import AdminTeams from "./AdminTeams";
import AdminMatchs from "./AdminMatchs";
import AdminMessages from "./AdminMessages";
import AdminPlayers from "./AdminPlayers";
import AdminNews from "./AdminNews";
import AdminEvent from "./AdminEvent";
import AdminCalendar from "./AdminCalendar";
import AdminSettings from "./AdminSettings";
import AdminAccess from "./AdminAccess";
import AdminLogistics from "./AdminLogistics";
import AdminStaff from "./AdminStaff";
import AdminContent from "./AdminContent";
import AdminDetections from "./AdminDetections";
import AdminCRM from "./AdminCRM";
import AdminManualReg from "./AdminManualReg";
import AdminAffiliates from "./AdminAffiliates";
import AdminPaperForm from "./AdminPaperForm";
import AdminJobs from "./AdminJobs";
import AdminHowto from "./AdminHowto";
import AdminFactures from "./AdminFactures";
import AdminFinances from "./AdminFinances";
import AdminEffectif from "./AdminEffectif";
import AdminReservistes from "./AdminReservistes";
import AdminJoueursDupliques from "./AdminJoueursDupliques";
import AdminDistinctions from "./AdminDistinctions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const allTabs = [
  { id: "crm", label: "CRM", icon: BarChart3, roles: ["admin", "membre"] },
  { id: "manuelle", label: "Inscription manuelle", icon: UserPlus, roles: ["admin", "membre"] },
  { id: "formulaire", label: "Formulaire papier", icon: Printer, roles: ["admin", "membre"] },
  { id: "inscriptions", label: "Inscriptions", icon: Users, roles: ["admin"] },
  { id: "detections", label: "Détections", icon: Radar, roles: ["admin"] },
  { id: "rdv", label: "Rendez-vous", icon: CalendarCheck, roles: ["admin"] },
  { id: "messages", label: "Messages", icon: Mail, roles: ["admin", "secretaire"] },
  { id: "equipes", label: "Équipes", icon: Shield, roles: ["admin"] },
  { id: "effectif", label: "Effectif", icon: ListChecks, roles: ["admin"] },
  { id: "reservistes", label: "Réservistes", icon: Users, roles: ["admin"] },
  { id: "dupliques", label: "Joueurs dupliqués", icon: Copy, roles: ["admin"] },
  { id: "matchs", label: "Matchs", icon: Goal, roles: ["admin"] },
  { id: "distinctions", label: "Mur des Légendes", icon: Trophy, roles: ["admin"] },
  { id: "logistique", label: "Logistique", icon: Package, roles: ["admin"] },
  { id: "joueurs", label: "Joueurs", icon: Trophy, roles: ["admin"] },
  { id: "staff", label: "Staff", icon: UserRound, roles: ["admin"] },
  { id: "actualites", label: "Actualités", icon: Newspaper, roles: ["admin", "membre"] },
  { id: "affiliation", label: "Affiliation", icon: HandCoins, roles: ["admin"] },
  { id: "emplois", label: "Offres d'emploi", icon: Briefcase, roles: ["admin"] },
  { id: "tutoriel", label: "Tutoriel", icon: Film, roles: ["admin"] },
  { id: "factures", label: "Factures", icon: Receipt, roles: ["admin"] },
  { id: "finances", label: "Finances", icon: Wallet, roles: ["admin"] },
  { id: "evenement", label: "Événement", icon: CalendarDays, roles: ["admin"] },
  { id: "contenu", label: "Contenu", icon: FileText, roles: ["admin"] },
  { id: "reglages", label: "Réglages", icon: Settings, roles: ["admin"] },
  { id: "acces", label: "Accès", icon: KeyRound, roles: ["admin"] },
];

export default function AdminDashboard({ user, onLogout }) {
  const tabs = allTabs.filter((t) => user.role === "admin" || (user.tabs_effectifs || []).includes(t.id));
  const [tab, setTab] = useState("crm");
  const tabBarRef = useRef(null);
  const [canScrollTabs, setCanScrollTabs] = useState(false);

  useEffect(() => {
    const el = tabBarRef.current;
    if (!el) return;
    const check = () => setCanScrollTabs(el.scrollWidth > el.clientWidth + el.scrollLeft + 8);
    check();
    el.addEventListener("scroll", check);
    window.addEventListener("resize", check);
    return () => { el.removeEventListener("scroll", check); window.removeEventListener("resize", check); };
  }, [tabs.length]);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    onLogout();
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0C] text-white">
      <header className="border-b border-[#27272A] bg-[#121214] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/assets/logo-dark-sm.png" alt="D2R" className="h-9 w-auto" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">Admin</span>
            {user.role === "membre" && (
              <span className="text-[9px] uppercase tracking-widest border border-sky-500/50 text-sky-400 px-2 py-0.5" data-testid="role-badge-membre">Membre</span>
            )}
          </div>
          <div className="flex items-center gap-6">
            <a href="/" data-testid="admin-view-site" className="text-xs text-[#71717A] hover:text-[#C5A059] transition-colors">Voir le site</a>
            <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 text-xs text-[#A1A1AA] hover:text-white transition-colors">
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="font-display text-3xl uppercase font-bold">
          Bonjour, <span className="text-[#C5A059]">{user.nom || "Admin"}</span>
        </h1>
        {user.role === "membre" && (
          <p className="text-xs text-[#71717A] mt-2">Accès membre : CRM (lecture), inscriptions manuelles et actualités.</p>
        )}

        <div className="relative">
          <div ref={tabBarRef} className="flex gap-2 mt-8 border-b border-[#27272A] overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.2em] whitespace-nowrap border-b-2 transition-colors duration-300 ${
                tab === t.id ? "border-[#C5A059] text-[#C5A059]" : "border-transparent text-[#71717A] hover:text-white"
              }`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
          </div>
          {canScrollTabs && (
            <button
              onClick={() => tabBarRef.current?.scrollBy({ left: 260, behavior: "smooth" })}
              data-testid="admin-tabs-scroll"
              aria-label="Voir plus d'onglets"
              className="absolute right-0 top-0 bottom-0 w-16 flex items-center justify-end pr-1 bg-gradient-to-l from-[#0A0A0C] via-[#0A0A0C]/90 to-transparent text-[#C5A059] hover:text-[#E5C07B] transition-colors"
            >
              <ChevronRight size={18} />
            </button>
          )}
        </div>

        <div className="py-10">
          {tab === "crm" && <AdminCRM readOnly={user.role === "membre"} />}
          {tab === "manuelle" && <AdminManualReg />}
          {tab === "formulaire" && <AdminPaperForm readOnly={user.role === "membre"} />}
          {tab === "inscriptions" && <AdminRegistrations />}
          {tab === "detections" && <AdminDetections />}
          {tab === "rdv" && <AdminCalendar />}
          {tab === "equipes" && <AdminTeams />}
          {tab === "effectif" && <AdminEffectif />}
          {tab === "reservistes" && <AdminReservistes />}
          {tab === "dupliques" && <AdminJoueursDupliques />}
          {tab === "matchs" && <AdminMatchs />}
          {tab === "distinctions" && <AdminDistinctions />}
          {tab === "messages" && <AdminMessages />}
          {tab === "logistique" && <AdminLogistics />}
          {tab === "joueurs" && <AdminPlayers />}
          {tab === "staff" && <AdminStaff />}
          {tab === "actualites" && <AdminNews />}
          {tab === "affiliation" && <AdminAffiliates />}
          {tab === "emplois" && <AdminJobs />}
          {tab === "tutoriel" && <AdminHowto />}
          {tab === "factures" && <AdminFactures />}
          {tab === "finances" && <AdminFinances />}
          {tab === "evenement" && <AdminEvent />}
          {tab === "contenu" && <AdminContent />}
          {tab === "reglages" && <AdminSettings />}
          {tab === "acces" && <AdminAccess currentUser={user} />}
        </div>
      </div>
    </div>
  );
}
